import { useEffect, useState } from "react";
import { api, timeOf } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { Baby, Printer } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Photo } from "@/components/Photo";

const label = { in: "Checked in", out: "Checked out", absent: "Absent" };

export default function ParentHome() {
  const { user } = useAuth();
  const [kids, setKids] = useState([]);
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [feed, setFeed] = useState([]);
  const [att, setAtt] = useState([]);
  const [sel, setSel] = useState("all");

  useEffect(() => { api.get("/students").then(({ data }) => setKids(data)); }, []);

  useEffect(() => {
    const params = { day, ...(sel !== "all" ? { student_id: sel } : {}) };
    api.get("/activities", { params }).then(({ data }) => setFeed(data));
    api.get("/attendance", { params }).then(({ data }) => setAtt(data));
  }, [day, sel]);

  return (
    <div data-testid="parent-portal">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Family Portal</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight">Hi {user?.name?.split(" ")[0]}!</h1>
      <p className="mt-3 text-muted-foreground">Your child's day at Teach Play to Learn.</p>

      <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="my-children">
        {kids.length === 0 && (
          <p className="text-muted-foreground">
            No children linked to {user?.email} yet — our team will link them once enrollment is confirmed.
          </p>
        )}
        {kids.map((k) => (
          <div key={k.id} className="bg-white rounded-2xl border border-border p-6" data-testid={`child-card-${k.id}`}>
            <span className="h-12 w-12 rounded-2xl bg-blue-50 text-[#3B82F6] grid place-items-center">
              <Baby size={26} weight="duotone" />
            </span>
            <p className="mt-5 font-display text-lg font-semibold">{k.first_name} {k.last_name}</p>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#3B82F6] mt-1">{k.program}</p>
            <Link to={`/portal/daysheet/${k.id}?day=${day}`}>
              <Button size="sm" variant="outline" className="mt-4 rounded-full font-bold w-full"
                      data-testid={`view-daysheet-${k.id}`}>
                <Printer size={15} className="mr-2" /> Printable day sheet
              </Button>
            </Link>
            <dl className="mt-4 space-y-2 text-sm">
              {[["Room", k.room], ["Check-in code", k.checkin_code], ["Allergies", k.allergies || "None"]].map(([a, b]) => (
                <div key={a} className="flex justify-between gap-3">
                  <dt className="text-muted-foreground font-semibold">{a}</dt><dd className="font-semibold">{b}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap gap-4 items-end">
        <div>
          <label className="text-sm font-bold block">Date</label>
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)}
                 className="mt-2 h-11 rounded-xl border border-input px-3 bg-white" data-testid="report-date" />
        </div>
        <div className="w-56">
          <label className="text-sm font-bold block">Child</label>
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger className="mt-2 h-11 rounded-xl bg-white" data-testid="report-child"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All children</SelectItem>
              {kids.map((k) => <SelectItem key={k.id} value={k.id}>{k.first_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-border p-6" data-testid="daily-report">
          <h2 className="font-display text-xl font-semibold">Daily report</h2>
          <div className="mt-5 space-y-4">
            {feed.length === 0 && <p className="text-sm text-muted-foreground">Nothing logged for this day yet.</p>}
            {feed.map((a) => (
              <div key={a.id} className="border-b border-border pb-3">
                <p className="text-sm font-bold">{a.student_name} · <span className="capitalize text-[#3B82F6]">{a.type}</span></p>
                {a.photo_path && (
                  <Photo path={a.photo_path} alt={a.detail || "Photo from today"}
                         className="mt-2 rounded-2xl w-full h-56 object-cover"
                         testid={`parent-photo-${a.id}`} />
                )}
                {a.detail && <p className="text-sm text-muted-foreground mt-1">{a.detail}</p>}
                <p className="text-xs text-muted-foreground mt-1">{timeOf(a.at)} · {a.staff}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-border p-6" data-testid="attendance-history">
          <h2 className="font-display text-xl font-semibold">Check-in history</h2>
          <div className="mt-5 space-y-3">
            {att.length === 0 && <p className="text-sm text-muted-foreground">No attendance recorded for this day.</p>}
            {att.map((a) => (
              <div key={a.id} className="flex justify-between text-sm border-b border-border pb-3">
                <span className="font-bold">{a.student_name}</span>
                <span className="text-muted-foreground font-semibold">{label[a.kind]} · {timeOf(a.at)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
